/**
 * Match Intelligence Experience — deterministic story layer on set aggregates.
 * No AI / point-log / serve telemetry required. Sport modules may append via
 * StatisticsProvider.projectMatchIntelligence.
 */

import { buildMatchInsightStats, buildGameScoreBars } from "./buildMatchInsightStats";
import { buildPointFlowModel } from "./buildRallyIntelligence";
import { normalizeSportId, DEFAULT_SPORT } from "../sport/sportTypes";

/**
 * @typedef {{
 *   id: string,
 *   emoji: string,
 *   title: string,
 *   summary: string,
 *   tone: string,
 * }} MatchStory
 *
 * @typedef {{
 *   id: string,
 *   label: string,
 *   detail: string,
 *   tone: string,
 * }} MatchDna
 *
 * @typedef {{
 *   id: string,
 *   label: string,
 *   detail: string,
 *   tone: string,
 *   icon: string,
 * }} MomentumPhase
 *
 * @typedef {{
 *   id: string,
 *   title: string,
 *   detail: string,
 *   tone: string,
 * }} TurningPoint
 *
 * @typedef {{
 *   id: string,
 *   label: string,
 *   value: string,
 *   detail?: string,
 *   tone?: string,
 * }} MatchInsightCard
 *
 * @typedef {{
 *   id: string,
 *   title: string,
 *   detail: string,
 *   tone: string,
 *   outcome?: string,
 * }} PressureMoment
 *
 * @typedef {{
 *   id: string,
 *   title: string,
 *   detail: string,
 *   tone: string,
 *   kind: string,
 * }} CareerImpactItem
 */

/**
 * @param {object} insights from buildMatchInsightStats
 * @param {{ winnerSide?: string, durationSec?: number|null, resultForViewer?: 'win'|'loss'|null }} ctx
 * @returns {MatchStory}
 */
export function buildMatchStory(insights, ctx = {}) {
  const winner = ctx.winnerSide;
  const viewerWin = ctx.resultForViewer === "win";
  const viewerLoss = ctx.resultForViewer === "loss";
  const you = viewerWin || viewerLoss;
  const subject = you ? (viewerWin ? "You" : "You") : winner === "A" ? "Team A" : "Team B";
  const won = you ? viewerWin : Boolean(winner);

  if (insights.comeback && won) {
    return {
      id: "incredible_comeback",
      emoji: "💪",
      title: "Incredible Comeback",
      summary: you
        ? "You recovered from an early deficit to win the match."
        : "The winners recovered from an early deficit to take the match.",
      tone: "win",
    };
  }
  if (insights.sweep && (insights.dominanceA >= 58 || insights.dominanceB >= 58)) {
    return {
      id: "dominant_victory",
      emoji: "🏆",
      title: won ? "Dominant Victory" : "Dominant Defeat",
      summary: you
        ? viewerWin
          ? "You never trailed after taking the lead — straight games, clear control."
          : "Your opponent controlled the match from start to finish."
        : "A straight-games statement win with clear point share.",
      tone: viewerLoss ? "loss" : "win",
    };
  }
  if (insights.closestMargin != null && insights.closestMargin <= 2 && insights.games?.length) {
    return {
      id: "nail_biter",
      emoji: "🔥",
      title: "Nail Biter",
      summary: you
        ? viewerWin
          ? "You survived a late battle to close out a tight match."
          : "It came down to the wire — a narrow loss in a tense match."
        : "Every game stayed close — a razor-thin result.",
      tone: "amber",
    };
  }

  const first = insights.games?.[0];
  const last = insights.games?.[insights.games.length - 1];
  if (first && won && first.winner === winner && first.margin >= 6) {
    return {
      id: "perfect_start",
      emoji: "⚡",
      title: "Perfect Start",
      summary: you
        ? "You seized Game 1 decisively and set the tone for the match."
        : "A decisive opening game shaped the rest of the match.",
      tone: "win",
    };
  }
  if (
    last &&
    first &&
    won &&
    last.winner === winner &&
    last.margin <= 3 &&
    first.margin >= 5
  ) {
    return {
      id: "clinical_finish",
      emoji: "🎯",
      title: "Clinical Finish",
      summary: you
        ? "You closed out under pressure after building an early cushion."
        : "A composed finish after an early lead.",
      tone: "win",
    };
  }

  const mins =
    ctx.durationSec != null && ctx.durationSec > 0
      ? Math.round(ctx.durationSec / 60)
      : null;
  if ((mins != null && mins >= 45) || (insights.totalPoints || 0) >= 90) {
    return {
      id: "marathon_battle",
      emoji: "⚔",
      title: "Marathon Battle",
      summary: you
        ? viewerWin
          ? "A long, grinding battle — endurance decided it."
          : "A marathon match that slipped away late."
        : "A long, high-volume contest of endurance.",
      tone: "purple",
    };
  }
  if (mins != null && mins <= 15 && insights.sweep) {
    return {
      id: "blitz",
      emoji: "⚡",
      title: "Lightning Win",
      summary: you
        ? viewerWin
          ? "A short, sharp statement — over before they settled."
          : "The match ended quickly against you."
        : "A short, decisive contest.",
      tone: viewerLoss ? "loss" : "win",
    };
  }

  if (won) {
    return {
      id: "solid_win",
      emoji: "✨",
      title: "Solid Victory",
      summary: you
        ? "You took care of business and added another win to the ledger."
        : `${subject} earned a clean win.`,
      tone: "win",
    };
  }
  return {
    id: "tough_loss",
    emoji: "🌤",
    title: "Tough Outing",
    summary: you
      ? "Not your day — every match still feeds your career story."
      : "A hard-fought contest with a clear winner.",
    tone: "muted",
  };
}

/**
 * One primary Match DNA personality.
 * @param {object} insights
 * @param {{ durationSec?: number|null, sportHints?: MatchDna[] }} [opts]
 * @returns {MatchDna}
 */
export function classifyMatchDna(insights, opts = {}) {
  const sportHint = (opts.sportHints || []).find(Boolean);
  if (sportHint) return sportHint;

  const mins =
    opts.durationSec != null && opts.durationSec > 0
      ? Math.round(opts.durationSec / 60)
      : null;
  const dom = Math.max(insights.dominanceA || 0, insights.dominanceB || 0);

  if (insights.comeback) {
    return {
      id: "comeback",
      label: "Comeback",
      detail: "Recovered after dropping the opener",
      tone: "amber",
    };
  }
  if (insights.sweep && dom >= 58) {
    return {
      id: "dominant",
      label: "Dominant",
      detail: `${dom}% of points won by the winners`,
      tone: "win",
    };
  }
  if (mins != null && mins >= 45) {
    return {
      id: "marathon",
      label: "Marathon",
      detail: `${mins}-minute grind`,
      tone: "purple",
    };
  }
  if (mins != null && mins <= 15) {
    return {
      id: "fast",
      label: "Fast Match",
      detail: `Wrapped in ${mins} minutes`,
      tone: "purple",
    };
  }
  if ((insights.totalPoints || 0) >= 80) {
    return {
      id: "high_scoring",
      label: "High Scoring",
      detail: `${insights.totalPoints} points exchanged`,
      tone: "amber",
    };
  }
  if (insights.closestMargin != null && insights.closestMargin <= 2) {
    return {
      id: "high_pressure",
      label: "High Pressure",
      detail: "Games decided by the finest margins",
      tone: "amber",
    };
  }
  if (insights.biggestMargin != null && insights.biggestMargin >= 10) {
    return {
      id: "aggressive",
      label: "Aggressive",
      detail: `Peak game margin ${insights.biggestMargin}`,
      tone: "win",
    };
  }
  if (dom >= 45 && dom <= 55) {
    return {
      id: "balanced",
      label: "Balanced",
      detail: "Point share stayed nearly even",
      tone: "muted",
    };
  }
  if (mins != null && mins >= 30) {
    return {
      id: "long",
      label: "Long Match",
      detail: `${mins} minutes of play`,
      tone: "muted",
    };
  }
  if (insights.pointsPerMin != null && insights.pointsPerMin < 4) {
    return {
      id: "defensive",
      label: "Defensive",
      detail: "Low scoring pace — patient exchanges",
      tone: "muted",
    };
  }
  return {
    id: "competitive",
    label: "Competitive",
    detail: "A contested sporting story",
    tone: "muted",
  };
}

/**
 * Divide the match into narrative momentum phases (game-level).
 * @param {object} insights
 * @param {{ winnerSide?: string, teamALabel?: string, teamBLabel?: string }} ctx
 * @returns {MomentumPhase[]}
 */
export function buildMomentumPhases(insights, ctx = {}) {
  const games = insights.games || [];
  if (!games.length) return [];

  /** @type {MomentumPhase[]} */
  const phases = [];
  let leadSide = null;

  games.forEach((g, i) => {
    const margin = g.margin;
    const tight = margin <= 2;
    const blowout = margin >= 8;
    let label = "Neck-and-Neck";
    let icon = "↔";
    let tone = "muted";
    let detail = `${g.scoreA}–${g.scoreB}`;

    if (i === 0 && blowout && g.winner) {
      label = "Strong Start";
      icon = "⚡";
      tone = g.winner === "A" ? "a" : "b";
      detail = `${g.winner === "A" ? ctx.teamALabel || "Team A" : ctx.teamBLabel || "Team B"} seized Game 1`;
    } else if (blowout && g.winner) {
      label = "Opponent Dominated";
      icon = "🔥";
      tone = g.winner === "A" ? "a" : "b";
      detail = `Game ${i + 1} margin ${margin}`;
    } else if (tight) {
      label = "Neck-and-Neck";
      icon = "⚔";
      tone = "amber";
      detail = `Game ${i + 1} decided by ${margin}`;
    } else if (g.winner && leadSide && g.winner !== leadSide) {
      label = "Momentum Shift";
      icon = "↗";
      tone = "purple";
      detail = `Game ${i + 1} flipped the run`;
    } else if (g.winner) {
      label = g.winner === ctx.winnerSide && i === games.length - 1 ? "Victory Lap" : "Control";
      icon = g.winner === ctx.winnerSide && i === games.length - 1 ? "🏆" : "●";
      tone = g.winner === "A" ? "a" : "b";
      detail = `${g.label} · ${g.scoreA}–${g.scoreB}`;
    }

    if (i === games.length - 1 && ctx.winnerSide) {
      if (g.winner === ctx.winnerSide) {
        label = "Victory";
        icon = "🏆";
        tone = "win";
        detail = "Match closed out";
      }
    }

    phases.push({
      id: `phase_${i}`,
      label,
      detail,
      tone,
      icon,
    });
    if (g.winner) leadSide = g.winner;
  });

  return phases;
}

/**
 * Major turning points from set aggregates.
 * @param {object} insights
 * @param {{ winnerSide?: string, teamALabel?: string, teamBLabel?: string }} ctx
 * @returns {TurningPoint[]}
 */
export function buildTurningPoints(insights, ctx = {}) {
  const games = insights.games || [];
  /** @type {TurningPoint[]} */
  const points = [];

  if (insights.comeback && games[0]) {
    points.push({
      id: "tp_comeback",
      title: "Recovered after Game 1",
      detail: `Dropped ${games[0].scoreA}–${games[0].scoreB}, then flipped the match`,
      tone: "amber",
    });
  }

  if (insights.biggestMargin != null && insights.biggestMargin >= 6) {
    const blow = games.find((g) => g.margin === insights.biggestMargin);
    if (blow) {
      points.push({
        id: "tp_blowout_game",
        title: `Largest swing · ${blow.label}`,
        detail: `${blow.scoreA}–${blow.scoreB} (margin ${blow.margin})`,
        tone: "purple",
      });
    }
  }

  if (insights.closestMargin != null && insights.closestMargin <= 2) {
    const tight = games.find((g) => g.margin === insights.closestMargin);
    if (tight) {
      points.push({
        id: "tp_tight",
        title: `Pressure game · ${tight.label}`,
        detail: `Decided by ${tight.margin} point${tight.margin === 1 ? "" : "s"}`,
        tone: "amber",
      });
    }
  }

  if (games.length >= 3) {
    const last = games[games.length - 1];
    points.push({
      id: "tp_decider",
      title: "Deciding game",
      detail: `${last.scoreA}–${last.scoreB} sealed the result`,
      tone: "win",
    });
  } else if (games.length && ctx.winnerSide) {
    const last = games[games.length - 1];
    if (last.winner === ctx.winnerSide) {
      points.push({
        id: "tp_closeout",
        title: "Match closed out",
        detail: `Final scoreline ${last.scoreA}–${last.scoreB}`,
        tone: "win",
      });
    }
  }

  const winnerPts =
    ctx.winnerSide === "A"
      ? insights.pointsA
      : ctx.winnerSide === "B"
        ? insights.pointsB
        : null;
  const loserPts =
    ctx.winnerSide === "A"
      ? insights.pointsB
      : ctx.winnerSide === "B"
        ? insights.pointsA
        : null;
  if (winnerPts != null && loserPts != null && winnerPts - loserPts >= 12) {
    points.push({
      id: "tp_point_gap",
      title: "Point gap opened",
      detail: `Winners outscored by ${winnerPts - loserPts} overall`,
      tone: "win",
    });
  }

  return points.slice(0, 5);
}

/**
 * Deterministic insight cards (set-level only).
 * @param {object} insights
 * @param {{ durationSec?: number|null, sportInsights?: MatchInsightCard[] }} [opts]
 * @returns {MatchInsightCard[]}
 */
export function buildMatchInsightCards(insights, opts = {}) {
  /** @type {MatchInsightCard[]} */
  const cards = [];
  const games = insights.games || [];

  if (insights.biggestMargin != null) {
    const g = games.find((x) => x.margin === insights.biggestMargin);
    cards.push({
      id: "largest_lead",
      label: "Largest lead",
      value: String(insights.biggestMargin),
      detail: g ? `${g.label} · ${g.scoreA}–${g.scoreB}` : undefined,
      tone: "win",
    });
  }
  if (insights.closestMargin != null) {
    cards.push({
      id: "tightest_game",
      label: "Tightest game",
      value: String(insights.closestMargin),
      detail: "Smallest deciding margin",
      tone: "amber",
    });
  }
  if (insights.comeback) {
    const g1 = games[0];
    const deficit = g1 ? Math.abs(g1.scoreA - g1.scoreB) : null;
    cards.push({
      id: "largest_comeback",
      label: "Comeback",
      value: deficit != null ? `−${deficit} G1` : "Yes",
      detail: "Won after dropping the opener",
      tone: "amber",
    });
  }
  if (games.length) {
    const highest = [...games].sort(
      (a, b) => b.scoreA + b.scoreB - (a.scoreA + a.scoreB)
    )[0];
    cards.push({
      id: "highest_scoring_game",
      label: "Highest scoring game",
      value: `${highest.scoreA + highest.scoreB}`,
      detail: `${highest.label} · ${highest.scoreA}–${highest.scoreB}`,
      tone: "purple",
    });
  }
  if (insights.pointsPerMin != null) {
    cards.push({
      id: "pace",
      label: "Match pace",
      value: `${insights.pointsPerMin}`,
      detail: "Points per minute",
      tone: "muted",
    });
  }
  if (insights.dominanceA != null && insights.dominanceB != null) {
    const top = insights.dominanceA >= insights.dominanceB ? insights.dominanceA : insights.dominanceB;
    cards.push({
      id: "point_share",
      label: "Peak point share",
      value: `${top}%`,
      detail: "Highest share of total points",
      tone: "win",
    });
  }

  (opts.sportInsights || []).forEach((c) => {
    if (c && !cards.some((x) => x.id === c.id)) cards.push(c);
  });

  return cards.slice(0, 8);
}

/**
 * Pressure moments inferred from set scores (no point log).
 * @param {object} insights
 * @param {{ sportId?: string, winnerSide?: string }} ctx
 * @returns {PressureMoment[]}
 */
export function buildPressureMoments(insights, ctx = {}) {
  const sportId = normalizeSportId(ctx.sportId || DEFAULT_SPORT);
  const games = insights.games || [];
  /** @type {PressureMoment[]} */
  const moments = [];

  const deuceFloor =
    sportId === "tennis" ? 6 : sportId === "pickleball" || sportId === "table_tennis" ? 9 : 18;

  games.forEach((g) => {
    const bothHigh = g.scoreA >= deuceFloor && g.scoreB >= deuceFloor;
    const close = g.margin <= 2;
    if (bothHigh && close) {
      moments.push({
        id: `pressure_deuce_${g.index}`,
        title: sportId === "tennis" ? "Set pressure" : "Deuce territory",
        detail: `${g.label} reached ${g.scoreA}–${g.scoreB}`,
        tone: "amber",
        outcome: g.winner
          ? g.winner === ctx.winnerSide
            ? "Converted"
            : "Dropped"
          : undefined,
      });
    } else if (close && g.winner) {
      moments.push({
        id: `pressure_close_${g.index}`,
        title: "Game point tension",
        detail: `${g.label} decided by ${g.margin}`,
        tone: "amber",
        outcome: g.winner === ctx.winnerSide ? "Held" : "Broken",
      });
    }
  });

  if (games.length >= 2 && ctx.winnerSide) {
    const last = games[games.length - 1];
    moments.push({
      id: "pressure_match_point",
      title: "Match point",
      detail: `Final game closed ${last.scoreA}–${last.scoreB}`,
      tone: "win",
      outcome: last.winner === ctx.winnerSide ? "Converted" : "Missed",
    });
  }

  return moments.slice(0, 6);
}

/**
 * Momentum river series — cumulative lead (A − B) after each game.
 * @param {object} insights
 */
export function buildMomentumRiver(insights) {
  const prog = insights.progression;
  if (!prog?.seriesA?.length) return null;
  const lead = prog.seriesA.map((a, i) => a - (prog.seriesB[i] || 0));
  const labels = ["Start", ...(prog.labels || [])];
  // Align labels: series includes start 0
  while (labels.length < lead.length) labels.push(`G${labels.length}`);
  const maxAbs = Math.max(1, ...lead.map((v) => Math.abs(v)));
  return {
    labels: labels.slice(0, lead.length),
    lead,
    maxAbs,
    crossings: lead
      .map((v, i, arr) => (i > 0 && Math.sign(v) !== Math.sign(arr[i - 1]) && v !== 0 ? i : -1))
      .filter((i) => i > 0),
  };
}

/**
 * Career impact cards for this match (projection filter).
 * @param {{
 *   matchId: string,
 *   completedAt?: number|null,
 *   sportId?: string,
 *   isWin?: boolean,
 *   durationSec?: number|null,
 *   cards?: object[],
 *   achievementFramework?: { unlocked?: object[] } | null,
 *   milestones?: object[],
 *   personalBests?: object[],
 *   stats?: object,
 * }} input
 * @returns {CareerImpactItem[]}
 */
export function buildCareerImpactForMatch(input) {
  const {
    matchId,
    completedAt,
    sportId,
    isWin,
    durationSec,
    cards = [],
    achievementFramework,
    milestones = [],
    personalBests = [],
    stats = {},
  } = input;
  /** @type {CareerImpactItem[]} */
  const items = [];
  const at = completedAt || null;

  const near = (ts) =>
    at != null && ts != null && Math.abs(Number(ts) - Number(at)) < 120000;

  (achievementFramework?.unlocked || []).forEach((a) => {
    if (near(a.unlockedAt)) {
      items.push({
        id: `ach_${a.id}`,
        title: "Achievement unlocked",
        detail: a.title,
        tone: "purple",
        kind: "achievement",
      });
    }
  });

  (milestones || []).forEach((m) => {
    if (near(m.at)) {
      items.push({
        id: `ms_${m.id}`,
        title: m.title,
        detail: m.detail || "Career milestone",
        tone: m.tone || "amber",
        kind: "milestone",
      });
    }
  });

  const chrono = [...cards].sort((a, b) => (a.completedAt || 0) - (b.completedAt || 0));
  const idx = chrono.findIndex((c) => c.matchId === matchId);
  if (idx === 0) {
    items.push({
      id: "ci_first_match",
      title: "First career match",
      detail: "Journey begins",
      tone: "amber",
      kind: "milestone",
    });
  }
  if (idx === 9) {
    items.push({
      id: "ci_10th",
      title: "10th career match",
      detail: "Volume milestone",
      tone: "amber",
      kind: "milestone",
    });
  }
  if (idx === 49) {
    items.push({
      id: "ci_50th",
      title: "50th career match",
      detail: "Half-century",
      tone: "amber",
      kind: "milestone",
    });
  }
  if (idx === 99) {
    items.push({
      id: "ci_100th",
      title: "100th career match",
      detail: "Century of matches",
      tone: "win",
      kind: "milestone",
    });
  }

  if (isWin && sportId) {
    const priorWins = chrono
      .slice(0, Math.max(0, idx))
      .filter((c) => c.isWin && normalizeSportId(c.sportId) === normalizeSportId(sportId));
    if (priorWins.length === 0 && idx >= 0) {
      items.push({
        id: "ci_first_sport_win",
        title: `First ${normalizeSportId(sportId).replace("_", " ")} victory`,
        detail: "Opened the win column in this sport",
        tone: "win",
        kind: "sport",
      });
    }
  }

  if (
    durationSec != null &&
    stats.longestMatchSec != null &&
    durationSec === stats.longestMatchSec
  ) {
    items.push({
      id: "ci_longest",
      title: "New personal record",
      detail: "Longest match",
      tone: "purple",
      kind: "record",
    });
  }
  if (
    durationSec != null &&
    stats.fastestMatchSec != null &&
    durationSec === stats.fastestMatchSec &&
    stats.fastestMatchSec !== stats.longestMatchSec
  ) {
    items.push({
      id: "ci_fastest",
      title: "New personal record",
      detail: "Shortest match",
      tone: "purple",
      kind: "record",
    });
  }

  if ((stats.currentWinningStreak || 0) >= 3 && isWin) {
    const thisCard = cards.find((c) => c.matchId === matchId);
    if (thisCard && cards[0]?.matchId === matchId) {
      items.push({
        id: "ci_streak",
        title: "New winning streak",
        detail: `${stats.currentWinningStreak}-match streak`,
        tone: "win",
        kind: "streak",
      });
    }
  }

  // Deduplicate by id
  const seen = new Set();
  return items.filter((it) => {
    if (seen.has(it.id)) return false;
    seen.add(it.id);
    return true;
  }).slice(0, 8);
}

/**
 * Story-centric timeline (start → phases → turning points → end).
 */
export function buildIntelligenceTimeline(input) {
  const {
    insights,
    story,
    phases = [],
    turningPoints = [],
    careerImpact = [],
    durationSec,
    winnerSide,
    teamALabel,
    teamBLabel,
  } = input;
  /** @type {Array<{ id: string, title: string, detail?: string, tone: string, kind: string }>} */
  const events = [];

  events.push({
    id: "tl_start",
    title: "Match start",
    detail: story?.title || "Underway",
    tone: "muted",
    kind: "start",
  });

  phases.forEach((p) => {
    events.push({
      id: `tl_${p.id}`,
      title: p.label,
      detail: p.detail,
      tone: p.tone,
      kind: "momentum",
    });
  });

  turningPoints.forEach((tp) => {
    events.push({
      id: `tl_${tp.id}`,
      title: tp.title,
      detail: tp.detail,
      tone: tp.tone,
      kind: "turning_point",
    });
  });

  careerImpact.forEach((c) => {
    events.push({
      id: `tl_${c.id}`,
      title: c.title,
      detail: c.detail,
      tone: c.tone,
      kind: "career",
    });
  });

  if (winnerSide) {
    events.push({
      id: "tl_final",
      title: "Final point",
      detail: `${winnerSide === "A" ? teamALabel || "Team A" : teamBLabel || "Team B"} win`,
      tone: "win",
      kind: "final",
    });
  }
  if (durationSec != null && durationSec > 0) {
    const mins = Math.round(durationSec / 60);
    events.push({
      id: "tl_duration",
      title: "Duration",
      detail: mins < 60 ? `${mins} minutes` : `${Math.floor(mins / 60)}h ${mins % 60}m`,
      tone: "muted",
      kind: "meta",
    });
  }

  // Fallback to game timeline pieces if phases empty
  if (!phases.length && insights?.timeline?.length) {
    insights.timeline.forEach((e) => {
      events.push({
        id: `legacy_${e.id}`,
        title: e.title,
        detail: e.detail,
        tone: e.tone,
        kind: "result",
      });
    });
  }

  const seen = new Set();
  return events.filter((e) => {
    if (seen.has(e.id)) return false;
    seen.add(e.id);
    return true;
  });
}

/**
 * Progressive statistics groups — overview first; empty advanced groups omitted.
 * @param {object} insights
 */
export function buildProgressiveStatGroups(insights) {
  const overview = (insights.quickStats || []).map((q) => ({
    label: q.label,
    value: q.value,
  }));
  const scoring = (insights.comparisonRows || []).map((r) => ({
    label: r.label,
    valueA: r.valueA,
    valueB: r.valueB,
  }));
  return {
    overview,
    scoring,
    serving: [],
    errors: [],
    challenges: [],
    sportSpecific: [],
  };
}

/**
 * AI-ready placeholder cards (deterministic content only for now).
 */
export function buildFutureAiSlots(story, dna) {
  return [
    {
      id: "coach_note",
      title: "Coach's Note",
      body: story?.summary || "Review this match when coaching tools arrive.",
      status: "deterministic",
    },
    {
      id: "performance_summary",
      title: "Performance Summary",
      body: dna
        ? `Match DNA: ${dna.label}. ${dna.detail}`
        : "Performance patterns will deepen with advanced logging.",
      status: "deterministic",
    },
  ];
}

/**
 * Full Match Intelligence bundle.
 * @param {object} input
 * @param {Array} input.sets
 * @param {string} [input.winnerSide]
 * @param {number|null} [input.durationSec]
 * @param {string} [input.teamALabel]
 * @param {string} [input.teamBLabel]
 * @param {string} [input.sportId]
 * @param {'win'|'loss'|null} [input.resultForViewer]
 * @param {object} [input.sportIntelligence] from Sport Module
 * @param {CareerImpactItem[]} [input.careerImpact]
 * @param {object|null} [input.rallySummary]
 */
export function buildMatchIntelligence(input) {
  const insights = buildMatchInsightStats({
    sets: input.sets,
    winnerSide: input.winnerSide,
    durationSec: input.durationSec,
    teamALabel: input.teamALabel,
    teamBLabel: input.teamBLabel,
  });

  const sport = input.sportIntelligence || {};
  const story = buildMatchStory(insights, {
    winnerSide: input.winnerSide,
    durationSec: input.durationSec,
    resultForViewer: input.resultForViewer ?? null,
  });
  const dna = classifyMatchDna(insights, {
    durationSec: input.durationSec,
    sportHints: sport.dnaHints || [],
  });
  const phases = buildMomentumPhases(insights, {
    winnerSide: input.winnerSide,
    teamALabel: input.teamALabel,
    teamBLabel: input.teamBLabel,
  });
  const turningPoints = [
    ...buildTurningPoints(insights, {
      winnerSide: input.winnerSide,
      teamALabel: input.teamALabel,
      teamBLabel: input.teamBLabel,
    }),
    ...(sport.turningPoints || []),
  ].slice(0, 6);
  const insightCards = buildMatchInsightCards(insights, {
    durationSec: input.durationSec,
    sportInsights: sport.insights || [],
  });

  const rallySummary = input.rallySummary || null;
  const pointFlow = buildPointFlowModel(rallySummary, insights);
  if (rallySummary?.longestServeStreak?.length) {
    const streak = rallySummary.longestServeStreak;
    const who =
      streak.playerName ||
      (streak.side === "A" ? input.teamALabel : input.teamBLabel) ||
      `Team ${streak.side}`;
    insightCards.unshift({
      id: "longest_serve_streak",
      label: "Longest serve run",
      value: String(streak.length),
      detail: `${who} · Game ${(streak.gameIndex ?? 0) + 1}`,
      tone: "win",
    });
  }
  if (pointFlow?.totalRallies) {
    insightCards.push({
      id: "total_rallies",
      label: "Rallies played",
      value: String(pointFlow.totalRallies),
      detail: "Full point sequence recorded",
      tone: "purple",
    });
  }

  const pressureMoments = [
    ...buildPressureMoments(insights, {
      sportId: input.sportId,
      winnerSide: input.winnerSide,
    }),
    ...(sport.pressureMoments || []),
  ].slice(0, 6);
  const careerImpact = input.careerImpact || [];
  const momentumRiver = buildMomentumRiver(insights);
  const timeline = buildIntelligenceTimeline({
    insights,
    story,
    phases,
    turningPoints,
    careerImpact,
    durationSec: input.durationSec,
    winnerSide: input.winnerSide,
    teamALabel: input.teamALabel,
    teamBLabel: input.teamBLabel,
  });
  const statGroups = buildProgressiveStatGroups(insights);
  if (sport.statExtras?.length) {
    statGroups.sportSpecific = sport.statExtras;
  }

  return {
    insights,
    story,
    dna,
    phases,
    turningPoints,
    insightCards: insightCards.slice(0, 10),
    pressureMoments,
    momentumRiver,
    pointFlow,
    rallySummary,
    careerImpact,
    timeline,
    statGroups,
    aiSlots: buildFutureAiSlots(story, dna),
    shareReady: true,
  };
}

/** —— Sport-optional Match Intelligence projectors —— */

export function projectBadmintonMatchIntelligence(record, insights) {
  const games = insights.games || buildGameScoreBars(record.sets || []);
  /** @type {object} */
  const out = { insights: [], turningPoints: [], pressureMoments: [], dnaHints: [], statExtras: [] };
  if (!games.length) return out;

  const longest = [...games].sort(
    (a, b) => b.scoreA + b.scoreB - (a.scoreA + a.scoreB)
  )[0];
  out.insights.push({
    id: "bd_longest_rally_proxy",
    label: "Longest game",
    value: `${longest.scoreA + longest.scoreB} pts`,
    detail: `${longest.label} (rally volume proxy)`,
    tone: "purple",
  });
  if (insights.biggestMargin >= 8) {
    out.insights.push({
      id: "bd_smash_dominance",
      label: "Smash dominance",
      value: `+${insights.biggestMargin}`,
      detail: "Largest game margin — attacking control",
      tone: "win",
    });
  }
  const tightNet = games.find((g) => g.margin <= 2 && g.scoreA + g.scoreB >= 36);
  if (tightNet) {
    out.insights.push({
      id: "bd_net_pressure",
      label: "Net pressure",
      value: `${tightNet.scoreA}–${tightNet.scoreB}`,
      detail: "High-volume tight finish",
      tone: "amber",
    });
  }
  return out;
}

export function projectTennisMatchIntelligence(record, insights) {
  const games = insights.games || buildGameScoreBars(record.sets || []);
  const out = { insights: [], turningPoints: [], pressureMoments: [], dnaHints: [], statExtras: [] };
  const tb = games.find(
    (g) => (g.scoreA === 7 && g.scoreB === 6) || (g.scoreA === 6 && g.scoreB === 7)
  );
  if (tb) {
    out.insights.push({
      id: "tn_tiebreak",
      label: "Tie-break set",
      value: `${tb.scoreA}–${tb.scoreB}`,
      detail: tb.label,
      tone: "amber",
    });
    out.dnaHints.push({
      id: "tiebreak_drama",
      label: "High Pressure",
      detail: "Featured a 7–6 set",
      tone: "amber",
    });
    out.turningPoints.push({
      id: "tp_tiebreak",
      title: "Tie-break set",
      detail: `${tb.label} went to 7–6`,
      tone: "amber",
    });
  }
  const longDeuce = games.find((g) => g.scoreA >= 6 && g.scoreB >= 6 && g.margin <= 2);
  if (longDeuce && !tb) {
    out.insights.push({
      id: "tn_long_deuce",
      label: "Long deuce battle",
      value: `${longDeuce.scoreA}–${longDeuce.scoreB}`,
      detail: longDeuce.label,
      tone: "amber",
    });
  }
  if (insights.biggestMargin >= 4) {
    out.insights.push({
      id: "tn_break_swing",
      label: "Break swing",
      value: `+${insights.biggestMargin}`,
      detail: "Largest set margin (break proxy)",
      tone: "win",
    });
  }
  return out;
}

export function projectPickleballMatchIntelligence(record, insights) {
  const games = insights.games || buildGameScoreBars(record.sets || []);
  const out = { insights: [], turningPoints: [], pressureMoments: [], dnaHints: [], statExtras: [] };
  if (insights.comeback) {
    out.insights.push({
      id: "pb_sideout_comeback",
      label: "Side-out comeback",
      value: "Yes",
      detail: "Won after dropping game one",
      tone: "amber",
    });
  }
  const kitchen = games.find((g) => g.margin <= 2 && Math.min(g.scoreA, g.scoreB) >= 9);
  if (kitchen) {
    out.insights.push({
      id: "pb_kitchen_pressure",
      label: "Kitchen pressure",
      value: `${kitchen.scoreA}–${kitchen.scoreB}`,
      detail: "Late-game tension at the NVZ",
      tone: "amber",
    });
    out.pressureMoments.push({
      id: "pb_kitchen",
      title: "Kitchen pressure",
      detail: `${kitchen.label} stayed tight late`,
      tone: "amber",
      outcome: kitchen.winner ? "Resolved" : undefined,
    });
  }
  if (insights.sweep) {
    out.insights.push({
      id: "pb_serve_streak_proxy",
      label: "Serve control",
      value: "Straight games",
      detail: "Held serve rhythm across games",
      tone: "win",
    });
  }
  return out;
}

export function projectTableTennisMatchIntelligence(record, insights) {
  const games = insights.games || buildGameScoreBars(record.sets || []);
  const out = { insights: [], turningPoints: [], pressureMoments: [], dnaHints: [], statExtras: [] };
  const deuce = games.find(
    (g) => g.scoreA >= 10 && g.scoreB >= 10 && Math.abs(g.scoreA - g.scoreB) >= 2
  );
  if (deuce) {
    out.insights.push({
      id: "tt_deuce",
      label: "Deuce performance",
      value: `${deuce.scoreA}–${deuce.scoreB}`,
      detail: deuce.label,
      tone: "amber",
    });
    out.pressureMoments.push({
      id: "tt_deuce_moment",
      title: "Deuce game",
      detail: `${deuce.label} past 10–10`,
      tone: "amber",
      outcome: deuce.winner === record.winnerSide ? "Converted" : "Dropped",
    });
  }
  const longest = [...games].sort(
    (a, b) => b.scoreA + b.scoreB - (a.scoreA + a.scoreB)
  )[0];
  if (longest) {
    out.insights.push({
      id: "tt_longest_rally_proxy",
      label: "Longest game",
      value: `${longest.scoreA + longest.scoreB}`,
      detail: longest.label,
      tone: "purple",
    });
  }
  if (insights.dominanceA >= 55 || insights.dominanceB >= 55) {
    out.insights.push({
      id: "tt_serve_dominance",
      label: "Serve dominance",
      value: `${Math.max(insights.dominanceA, insights.dominanceB)}%`,
      detail: "Point share under rally scoring",
      tone: "win",
    });
  }
  return out;
}
